import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule, formatDate } from '@angular/common';

import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';

import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { FiledropComponent } from '../../filedrop/filedrop.component';
import { NoteService } from 'src/app/services/note.service';
import { UserService } from 'src/app/services/user.service';
import { ActivityService } from 'src/app/services/activity.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { DialogManagerService } from 'src/app/services/dialog-manager.service';

import { Note } from 'src/app/models/note.model';
import { Course } from 'src/app/models/course.model';
import { user } from 'src/app/models/user.model';

@Component({
  selector: 'app-create-note-dialog',
  standalone: true,
  templateUrl: './create-note-dialog.component.html',
  styleUrls: ['./create-note-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    DialogModule,
    DropdownModule,
    EditorModule,
    ButtonModule,
    FloatLabelModule,
    InputTextModule,
    MultiSelectModule,

    FiledropComponent,
  ],
})
export class CreateNoteDialogComponent implements OnInit {
  @ViewChild(FiledropComponent) drop!: FiledropComponent;

  form!: FormGroup;
  courses: Course[] = [];
  participants: user[] = [];

  disableParticipant = true;
  submitting = false;

  selectedUser: user | null = null;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private note: NoteService,
    private usr: UserService,
    private act: ActivityService,
    private msg: NotificationService,
    private cfg: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private dlg: DialogManagerService,
  ) {}

  ngOnInit(): void {
    this.selectedUser = this.cfg.data?.selectedUser ?? null;
    this.buildForm();
    this.fetchCourses();
  }

  /* ---------- form ---------- */
  private buildForm(): void {
    const today = formatDate(new Date(), 'dd.MM.yyyy', 'de-DE');
    this.form = this.fb.group({
      title: [`Notiz - ${today}`, Validators.required],
      course: [null, Validators.required],
      participant: [[], Validators.required],
      text: [''],
    });
  }

  /* ---------- data ---------- */
  private fetchCourses(): void {
    const me = this.auth.getCurrentUserId();
    this.usr.getUserCoursesObservable(me).subscribe({
      next: (c) => {
        const interessent: Course = {
          courseId: -1,
          courseName: '--- Interessent:innen (ohne Kurs) ---',
          users: [],
          startDate: '',
          locationId: 0,
          courseTypeId: 0,
          isActive: true,
        };
        this.courses = [interessent, ...c];

        if (this.selectedUser) {
          const match = c.find((cs) =>
            cs.users.some((u) => u.userId === this.selectedUser!.userId),
          );
          this.form.patchValue({
            course: match?.courseId ?? -1,
            participant: [this.selectedUser.userId],
          });
          this.onCourse(this.form.value.course, true);
        }
      },
      error: () => this.msg.error('Kurse konnten nicht geladen werden'),
    });
  }

  onCourse(courseId: number | null, preserve = false): void {
    if (!preserve) {
      this.form.patchValue({ participant: [] });
    }

    this.disableParticipant = courseId == null;

    if (courseId === -1) {
      this.usr.getUserWithQuery(5).subscribe({
        next: (users) =>
          (this.participants = users.filter(
            (u) => u.isLead && !u.courses?.length,
          )),
        error: () =>
          this.msg.warn('Interessenten konnten nicht geladen werden'),
      });
      return;
    }

    const c = this.courses.find((cs) => cs.courseId === courseId);
    this.participants = c?.users.filter((u) => u.roleIds.includes(1)) ?? [];
    this.disableParticipant = this.participants.length === 0;
  }

  /* ---------- submit chain ---------- */
  submit(): void {
    if (this.form.invalid) return;
    this.submitting = true;
    this.drop.requestUpload();
  }

  uploadStart(): void {
    this.submitting = true;
  }

  uploadDone(urls: string[]): void {
    if (!this.submitting) return;
    this.submitting = false;

    const f = this.form.getRawValue();
    const ids: number[] = (f.participant as any[]).map((v: any) =>
      typeof v === 'object' ? v.userId : v,
    );

    if (!ids.length) {
      this.msg.warn('Mindestens einen Teilnehmer auswählen!');
      return;
    }

    const dto: Note = {
      creatorId: this.auth.getCurrentUserId(),
      title: f.title,
      remarks: f.text,
      courseId: f.course,
      userIds: ids,
      fileUrls: urls,
    };

    this.note.createNote(dto).subscribe({
      next: (a) => {
        this.act.addActivity(a);
        this.msg.success('Aktivität "Notiz" erfolgreich erstellt');
        this.close();
      },
      error: () => this.msg.error('Aktivität "Notiz" NICHT erstellt'),
    });
  }

  close(): void {
    this.dlg.closeNotizDialog();
    this.ref.close();
  }
}
